import React, { useState, useEffect } from 'react';
import {
  Search, 
  Plus, 
  Calendar, 
  Filter, 
  MoreVertical, 
  Edit, 
  Trash2,
  Eye,
  Phone,
  Mail,
  ShoppingCart,
  User,
  Clock,
  DollarSign,
  Package,
  Send
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Order, OrderQueryRequest } from '../types';
import apiService from '../services/api';
import toast from 'react-hot-toast';

const OrdersPage: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showActions, setShowActions] = useState<number | null>(null);

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'Created', label: 'Created' },
    { value: 'Assigned', label: 'Assigned' },
    { value: 'Service InProgress', label: 'Service InProgress' },
    { value: 'Service Done', label: 'Service Done' },
    { value: 'Payment Received', label: 'Payment Received' },
    { value: 'Cancelled', label: 'Cancelled' }
  ];

  useEffect(() => {
    loadOrders();
  }, [currentPage, search, statusFilter, startDate, endDate]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      console.log('Loading orders with params:', { 
        page: currentPage, 
        pageSize, 
        searchTerm: search, 
        status: statusFilter,
        startDate,
        endDate 
      });

      const response = await apiService.searchOrders(
        currentPage,
        pageSize,
        search || '',
        startDate ? new Date(startDate) : undefined,
        endDate ? new Date(endDate) : undefined,
        statusFilter || undefined
      );

      console.log('API Response:', response);

      if (response.isSuccess && response.entity) {
        console.log('Response entity:', response.entity);
        console.log('Items:', response.entity.items);
        
        const orders = response.entity.items || [];
        const totalCount = response.entity.totalCount || 0;
        
        setOrders(orders);
        setTotalCount(totalCount);
        
        console.log('Set orders:', orders.length, 'Total count:', totalCount);
      } else {
        console.error('API response not successful:', response);
        toast.error(response.errors?.join(', ') || 'Failed to load orders');
        setOrders([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error loading orders:', error);
      toast.error('Error connecting to server. Please check if the backend is running.');
      setOrders([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setSearch('');
    setStatusFilter('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  const getStatusBadge = (status: string) => {
    const statusColors: { [key: string]: string } = {
      'Created': 'bg-blue-100 text-blue-800',
      'Assigned': 'bg-yellow-100 text-yellow-800',
      'Service InProgress': 'bg-orange-100 text-orange-800',
      'Service Done': 'bg-green-100 text-green-800',
      'Payment Received': 'bg-emerald-100 text-emerald-800',
      'Cancelled': 'bg-red-100 text-red-800'
    };
    
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatCurrency = (amount: number) => {
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-IN');
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const handleDeleteOrder = async (orderId: number, orderNumber: string) => {
    if (confirm(`Are you sure you want to delete order ${orderNumber}? This action cannot be undone.`)) {
      try {
        const response = await apiService.deleteOrder(orderId);
        if (response.isSuccess) {
          toast.success('Order deleted successfully');
          loadOrders(); // Reload the orders list
        } else {
          toast.error(response.errors?.join(', ') || 'Failed to delete order');
        }
      } catch (error) {
        console.error('Error deleting order:', error);
        toast.error('Error deleting order');
      }
    }
  };

  const handleSendBill = async (orderId: number, orderNumber: string) => {
    try {
      const response = await apiService.sendOrderDetails(orderId);
      if (response.isSuccess) {
        toast.success(`Bill sent successfully for order ${orderNumber}`);
      } else {
        toast.error(response.errors?.join(', ') || 'Failed to send bill');
      }
    } catch (error) {
      console.error('Error sending bill:', error);
      toast.error('Error sending bill');
    }
  };

  const handleSendPdfBill = async (orderId: number, orderNumber: string) => {
    try {
      toast.loading(`Generating PDF for ${orderNumber}...`, { id: 'pdf-bill' });
      const response = await apiService.sendBill(orderId);
      if (response.isSuccess) {
        toast.success(`PDF bill sent successfully for ${orderNumber}`, { id: 'pdf-bill' });
      } else {
        toast.error(response.errors?.join(', ') || 'Failed to send PDF bill', { id: 'pdf-bill' });
      }
    } catch (error) {
      console.error('Error sending PDF bill:', error);
      toast.error('Error sending PDF bill', { id: 'pdf-bill' });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
            <ShoppingCart className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Orders</h1>
            <p className="text-sm text-gray-500">Manage customer orders and tracking</p>
          </div>
        </div>
        
        <Link
          to="/orders/create"
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Order
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search orders..."
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">From Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* End Date */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">To Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Filter Actions */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
          <div className="text-sm text-gray-500">
            Showing {orders.length} of {totalCount} orders
          </div>
          <button
            onClick={resetFilters}
            className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
          >
            Reset Filters
          </button>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingCart className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No orders found</h3>
            <p className="text-gray-500">Create your first order to get started.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Device</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{order.customerName}</div>
                          <div className="text-sm text-gray-500">{order.customerMobile}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{order.makeName}</div>
                          <div className="text-sm text-gray-500">{order.modelName}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(order.orderStatus)}`}>
                          {order.orderStatus}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{formatCurrency(order.finalAmount)}</div>
                          <div className="text-sm text-gray-500">{order.paymentMethod}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(order.orderDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="relative">
                          <button
                            onClick={() => setShowActions(showActions === order.id ? null : order.id)}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>
                          
                          {showActions === order.id && (
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-10">
                              <div className="py-1">
                                <Link
                                  to={`/orders/edit/${order.id}`}
                                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                  onClick={() => setShowActions(null)}
                                >
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit Order
                                </Link>
                                <button
                                  onClick={() => {
                                    setSelectedOrder(order);
                                    setShowActions(null);
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  <Eye className="h-4 w-4 mr-2" />
                                  View Details
                                </button>
                                <button
                                  onClick={() => {
                                    handleSendBill(order.id, order.orderNumber);
                                    setShowActions(null);
                                  }}
                                  disabled={order.orderStatus !== 'Payment Received'}
                                  className={`flex items-center w-full px-4 py-2 text-sm ${
                                    order.orderStatus === 'Payment Received'
                                      ? 'text-green-600 hover:bg-green-50'
                                      : 'text-gray-400 cursor-not-allowed'
                                  }`}
                                >
                                  <Send className="h-4 w-4 mr-2" />
                                  Send Bill
                                </button>
                                <button
                                  onClick={() => {
                                    handleSendPdfBill(order.id, order.orderNumber);
                                    setShowActions(null);
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-blue-600 hover:bg-blue-50"
                                >
                                  <Send className="h-4 w-4 mr-2" />
                                  Send PDF Bill
                                </button>
                                <button
                                  onClick={() => handleDeleteOrder(order.id, order.orderNumber)}
                                  className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete Order
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>


          </>
        )}
      </div>

      {/* Pagination */}
      {!loading && orders.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage <= 1}
                className="px-3 py-1 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const page = Math.max(1, currentPage - 2) + i;
                if (page > totalPages) return null;
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1 text-sm font-medium rounded-md ${
                      page === currentPage
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
              
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage >= totalPages}
                className="px-3 py-1 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersPage; 
